import { formatCents, formatCredits } from "@models/billing";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineRounded";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useStartUpgrade } from "../hooks/useBilling";

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  /** When set, lock the selector to this plan (e.g. opening straight to "Team"). */
  initialPlan?: "pro" | "team";
}

const PLANS = [
  {
    id: "pro" as const,
    name: "Pro",
    price_cents: 2500,
    credits: 2_500,
    seats: "1 user",
    icon: PersonIcon,
    bullets: [
      "All design and analysis features",
      "Sonnet 4.6 + Opus 4.7 access",
      "Top-up packs and auto-recharge",
      "Credits roll over for one cycle",
    ],
  },
  {
    id: "team" as const,
    name: "Team",
    price_cents: 3000,
    credits: 5_000,
    seats: "2+ seats",
    icon: GroupsIcon,
    bullets: [
      "Everything in Pro, per seat",
      "Shared org wallet across the team",
      "Admin controls and per-seat usage",
      "Centralised invoicing",
    ],
  },
];

/**
 * Step-1: pick a plan. Step-2: pick seats (Team only). Step-3: redirect
 * to Stripe Checkout. The mutation handles the redirect on success.
 */
export function UpgradeModal({
  open,
  onClose,
  initialPlan,
}: UpgradeModalProps) {
  const [planId, setPlanId] = useState<"pro" | "team">(initialPlan ?? "pro");
  const [seats, setSeats] = useState(2);
  const startUpgrade = useStartUpgrade();

  const team = PLANS.find((p) => p.id === "team");
  const teamTotal = (team?.price_cents ?? 3000) * Math.max(2, seats);
  const teamCredits = (team?.credits ?? 5000) * Math.max(2, seats);

  const handleConfirm = () => {
    startUpgrade.mutate(
      planId === "team" ? { planId, seats: Math.max(2, seats) } : { planId },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: "surface.default",
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ color: "text.disabled" }}>
            Upgrade
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Pick your plan
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mt: 1 }}
        >
          {PLANS.map((p) => {
            const selected = planId === p.id;
            const Icon = p.icon;
            return (
              <Box
                key={p.id}
                component="button"
                type="button"
                onClick={() => setPlanId(p.id)}
                aria-pressed={selected}
                sx={{
                  flex: 1,
                  p: 2.5,
                  borderRadius: 2,
                  border: "2px solid",
                  borderColor: selected ? "primary.main" : "border.default",
                  bgcolor: selected ? "accent.softer" : "surface.raised",
                  textAlign: "left",
                  font: "inherit",
                  color: "inherit",
                  cursor: "pointer",
                  transition: (t) =>
                    `border-color ${t.motion.fast}, background-color ${t.motion.fast}`,
                  "&:hover": { borderColor: "primary.light" },
                  "&:focus-visible": {
                    outline: "none",
                    borderColor: "primary.main",
                  },
                  position: "relative",
                }}
              >
                {selected && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <CheckRoundedIcon
                      sx={{ fontSize: 14, color: "primary.contrastText" }}
                    />
                  </Box>
                )}
                <Icon sx={{ color: "primary.main", fontSize: 24, mb: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {p.name}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {p.seats}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0.5,
                    mt: 1.5,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, lineHeight: 1 }}
                  >
                    {formatCents(p.price_cents)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    /{p.id === "team" ? "seat/mo" : "month"}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  {formatCredits(p.credits)} credits
                  {p.id === "team" ? " per seat" : ""} included
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 2 }}>
                  {p.bullets.map((b) => (
                    <Box
                      key={b}
                      sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                    >
                      <CheckRoundedIcon
                        sx={{ fontSize: 16, color: "primary.main", mt: 0.25 }}
                      />
                      <Typography variant="body2">{b}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Stack>

        {planId === "team" && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 1.5,
              bgcolor: "surface.raised",
              border: "1px solid",
              borderColor: "border.default",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Team size</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Each seat gets {formatCredits(team?.credits ?? 5000)} credits
                  into the shared org wallet.
                </Typography>
              </Box>
              <TextField
                type="number"
                size="small"
                value={seats}
                onChange={(e) =>
                  setSeats(Math.max(2, Number(e.target.value) || 2))
                }
                inputProps={{
                  min: 2,
                  step: 1,
                  "aria-label": "Number of seats",
                }}
                sx={{ width: 100 }}
              />
            </Stack>
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "border.default",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Monthly total · {formatCredits(teamCredits)} credits
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {formatCents(teamTotal)}/mo
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={startUpgrade.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={startUpgrade.isPending}
        >
          {startUpgrade.isPending
            ? "Redirecting…"
            : `Continue to checkout · ${formatCents(planId === "team" ? teamTotal : 2500)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
